import type { QueryClient } from "@tanstack/react-query";
import { flushSync } from "react-dom";

import { QUERY_KEYS } from "@/core/api/query-keys";

/**
 * Invalidation ciblée après une écriture métier (POST / PUT / PATCH / DELETE).
 *
 * Matching préfixe TanStack Query v5 : `["MERCHANTS_LIST"]` couvre toutes
 * les paginations / filtres / selects (`QUERY_KEYS.MERCHANTS_LIST(params)`).
 * Ne PAS appeler `QUERY_KEYS.MERCHANTS_LIST()` sans params — le `undefined`
 * en 2ᵉ position ne matche pas les listes réelles.
 *
 * Toujours `return` ce helper depuis `onSuccess` (jamais `void`) : TanStack
 * Query v5 attend la promesse du hook avant le callback `mutate({ onSuccess })`
 * qui ferme le dialog. Sans await, le tableau garde le cache (`staleTime` 30 s,
 * `refetchOnWindowFocus: false`).
 *
 * Ne PAS brancher depuis `mutateData` : les lectures POST (`/all`, lookups,
 * `POST /fee-quotes`, URL présignées) passent aussi par `mutateData` et ne
 * doivent pas vider le cache.
 */
export type InvalidateAfterMutationOptions = {
  /**
   * Après un CREATE (item newest-first en tête de liste) : ramène les
   * `DataTableClar` montés à la page 1 **avant** le refetch, pour que la
   * query active soit celle qui contient le nouvel item.
   */
  resetListPage?: boolean;
};

type ListPageResetListener = () => void;

const listPageResetListeners = new Set<ListPageResetListener>();

/** Abonnement utilisé par `DataTableClar` pour revenir à `pageIndex` 0. */
export function subscribeListPageReset(
  listener: ListPageResetListener
): () => void {
  listPageResetListeners.add(listener);
  return () => {
    listPageResetListeners.delete(listener);
  };
}

function notifyListPageReset(): void {
  if (listPageResetListeners.size === 0) return;
  flushSync(() => {
    listPageResetListeners.forEach((listener) => listener());
  });
}

export function invalidateAfterMutation(
  queryClient: QueryClient,
  queryKeys: readonly (readonly unknown[])[],
  options?: InvalidateAfterMutationOptions
): Promise<void> {
  if (options?.resetListPage) {
    notifyListPageReset();
  }
  return Promise.all(
    queryKeys.map((queryKey) =>
      queryClient.invalidateQueries({
        queryKey: [...queryKey],
        refetchType: "active",
      })
    )
  ).then(() => undefined);
}

const list = (root: string): readonly [string] => [root];

/** Liste + fiche + sous-ressources susceptibles d'afficher le statut. */
export function merchantWriteCacheKeys(merchantId?: string) {
  const keys: (readonly unknown[])[] = [list("MERCHANTS_LIST")];
  if (merchantId) {
    keys.push(
      QUERY_KEYS.MERCHANT_DETAIL(merchantId),
      list("MERCHANT_SUB_MERCHANTS"),
      QUERY_KEYS.MERCHANT_STORES_BY_MERCHANT(merchantId),
      QUERY_KEYS.MERCHANT_QR_CODES_BY_MERCHANT(merchantId),
      QUERY_KEYS.MERCHANT_SALES_AGENTS_BY_MERCHANT(merchantId),
      QUERY_KEYS.MERCHANT_SETTLEMENT_CONFIG(merchantId),
      QUERY_KEYS.MERCHANT_FEE_OVERRIDES(merchantId)
    );
  } else {
    keys.push(list("MERCHANT_DETAIL"), list("MERCHANT_SUB_MERCHANTS"));
  }
  return keys;
}

export function merchantStoreCacheKeys(merchantId: string, storeId?: string) {
  const keys: (readonly unknown[])[] = [
    QUERY_KEYS.MERCHANT_STORES_BY_MERCHANT(merchantId),
  ];
  if (storeId) keys.push(QUERY_KEYS.MERCHANT_STORE_DETAIL(storeId));
  return keys;
}

export function merchantSalesAgentCacheKeys(
  merchantId?: string,
  salesAgentId?: string
) {
  const keys: (readonly unknown[])[] = [QUERY_KEYS.MERCHANT_SALES_AGENT_ME()];
  if (merchantId) {
    keys.push(QUERY_KEYS.MERCHANT_SALES_AGENTS_BY_MERCHANT(merchantId));
  }
  if (salesAgentId) {
    keys.push(QUERY_KEYS.MERCHANT_SALES_AGENT_DETAIL(salesAgentId));
  } else {
    keys.push(list("MERCHANT_SALES_AGENT_DETAIL"));
  }
  return keys;
}

export function merchantSalesCacheKeys(salesAgentId?: string) {
  const keys: (readonly unknown[])[] = [
    list("MERCHANT_SALES_LIST"),
    list("MERCHANT_SALES_BY_AGENT"),
  ];
  if (salesAgentId) {
    keys.push(QUERY_KEYS.MERCHANT_SALES_BY_AGENT(salesAgentId));
  }
  return keys;
}

export function walletWriteCacheKeys(walletId?: string) {
  const keys: (readonly unknown[])[] = [list("WALLET_LIST")];
  if (walletId) {
    keys.push(
      QUERY_KEYS.WALLET_DETAIL(walletId),
      QUERY_KEYS.WALLET_BALANCE(walletId)
    );
  } else {
    keys.push(list("WALLET_DETAIL"), list("WALLET_BALANCE"));
  }
  return keys;
}

export function walletTransactionCacheKeys(transactionId?: string) {
  const keys: (readonly unknown[])[] = [
    list("WALLET_TRANSACTIONS_LIST"),
    list("WALLET_BALANCE"),
    list("WALLET_LIST"),
    list("PAYROLL_RUNS_LIST"),
    list("FUNDRAISING_CONTRIBUTIONS_LIST"),
    list("WALLET_EMONEY_RECONCILIATION"),
  ];
  if (transactionId) {
    keys.push(QUERY_KEYS.WALLET_TRANSACTION_DETAIL(transactionId));
  } else {
    keys.push(list("WALLET_TRANSACTION_DETAIL"));
  }
  return keys;
}

/**
 * Phase 18.3 — Contestations de transfert (guide v3.1). `open` / `reject` /
 * restitution (reverse-by-reference) touchent la file des contestations, la
 * transaction contestée et les soldes (gel / dégel chez le bénéficiaire).
 */
export function walletTransactionDisputeCacheKeys(
  transactionDisputeId?: string,
  transactionId?: string
) {
  const keys: (readonly unknown[])[] = [
    list("WALLET_TRANSACTION_DISPUTES_LIST"),
    list("WALLET_TRANSACTIONS_LIST"),
    list("WALLET_BALANCE"),
    list("WALLET_LIST"),
  ];
  if (transactionDisputeId) {
    keys.push(QUERY_KEYS.WALLET_TRANSACTION_DISPUTE_DETAIL(transactionDisputeId));
  } else {
    keys.push(list("WALLET_TRANSACTION_DISPUTE_DETAIL"));
  }
  if (transactionId) {
    keys.push(QUERY_KEYS.WALLET_TRANSACTION_DETAIL(transactionId));
  } else {
    keys.push(list("WALLET_TRANSACTION_DETAIL"));
  }
  return keys;
}

export function kycPartyCacheKeys(partyId?: string) {
  const keys: (readonly unknown[])[] = [list("KYC_PARTIES_LIST")];
  if (partyId) {
    keys.push(
      QUERY_KEYS.KYC_PARTY_DETAIL(partyId),
      QUERY_KEYS.KYC_COMPANY_PROFILE(partyId)
    );
  } else {
    keys.push(list("KYC_PARTY_DETAIL"), list("KYC_COMPANY_PROFILE"));
  }
  return keys;
}

export function kycCaseCacheKeys(kycCaseId?: string) {
  const keys: (readonly unknown[])[] = [list("KYC_CASES_LIST")];
  if (kycCaseId) keys.push(QUERY_KEYS.KYC_CASE_DETAIL(kycCaseId));
  else keys.push(list("KYC_CASE_DETAIL"));
  return keys;
}

export function kycAgentCacheKeys(agentId?: string) {
  const keys: (readonly unknown[])[] = [list("KYC_AGENTS_LIST")];
  if (agentId) keys.push(QUERY_KEYS.KYC_AGENT_DETAIL(agentId));
  else keys.push(list("KYC_AGENT_DETAIL"));
  return keys;
}

export function feeRuleCacheKeys(feeRuleId?: string) {
  const keys: (readonly unknown[])[] = [list("FEE_RULES_LIST")];
  if (feeRuleId) keys.push(QUERY_KEYS.FEE_RULE_DETAIL(feeRuleId));
  else keys.push(list("FEE_RULE_DETAIL"));
  return keys;
}

export function feePromotionCacheKeys(promotionId?: string) {
  const keys: (readonly unknown[])[] = [list("FEE_PROMOTIONS_LIST")];
  if (promotionId) keys.push(QUERY_KEYS.FEE_PROMOTION_DETAIL(promotionId));
  else keys.push(list("FEE_PROMOTION_DETAIL"));
  return keys;
}

export function feeSubscriptionPlanCacheKeys(planId?: string) {
  const keys: (readonly unknown[])[] = [list("FEE_SUBSCRIPTION_PLANS_LIST")];
  if (planId) keys.push(QUERY_KEYS.FEE_SUBSCRIPTION_PLAN_DETAIL(planId));
  else keys.push(list("FEE_SUBSCRIPTION_PLAN_DETAIL"));
  return keys;
}

export function feeCompanySubscriptionCacheKeys(
  companySubscriptionId?: string
) {
  const keys: (readonly unknown[])[] = [list("FEE_COMPANY_SUBSCRIPTIONS_LIST")];
  if (companySubscriptionId) {
    keys.push(
      QUERY_KEYS.FEE_COMPANY_SUBSCRIPTION_DETAIL(companySubscriptionId)
    );
  } else {
    keys.push(list("FEE_COMPANY_SUBSCRIPTION_DETAIL"));
  }
  return keys;
}

export function ledgerJournalCacheKeys(journalEntryId?: string) {
  const keys: (readonly unknown[])[] = [list("LEDGER_JOURNAL_ENTRIES_LIST")];
  if (journalEntryId) {
    keys.push(QUERY_KEYS.LEDGER_JOURNAL_ENTRY_DETAIL(journalEntryId));
  } else {
    keys.push(list("LEDGER_JOURNAL_ENTRY_DETAIL"));
  }
  return keys;
}

export function ledgerChartCacheKeys(chartOfAccountId?: string) {
  const keys: (readonly unknown[])[] = [list("LEDGER_CHART_OF_ACCOUNTS_LIST")];
  if (chartOfAccountId) {
    keys.push(QUERY_KEYS.LEDGER_CHART_OF_ACCOUNT_DETAIL(chartOfAccountId));
  } else {
    keys.push(list("LEDGER_CHART_OF_ACCOUNT_DETAIL"));
  }
  return keys;
}

export function ledgerPostingTemplateCacheKeys(postingTemplateId?: string) {
  const keys: (readonly unknown[])[] = [list("LEDGER_POSTING_TEMPLATES_LIST")];
  if (postingTemplateId) {
    keys.push(QUERY_KEYS.LEDGER_POSTING_TEMPLATE_DETAIL(postingTemplateId));
  } else {
    keys.push(list("LEDGER_POSTING_TEMPLATE_DETAIL"));
  }
  return keys;
}

export function ledgerProductCodeCacheKeys(productCodeId?: string) {
  const keys: (readonly unknown[])[] = [list("LEDGER_PRODUCT_CODES_LIST")];
  if (productCodeId) {
    keys.push(
      QUERY_KEYS.LEDGER_PRODUCT_CODE_DETAIL(productCodeId),
      list("LEDGER_PRODUCT_CODE_BY_CODE")
    );
  } else {
    keys.push(
      list("LEDGER_PRODUCT_CODE_DETAIL"),
      list("LEDGER_PRODUCT_CODE_BY_CODE")
    );
  }
  return keys;
}

export function ledgerProviderCacheKeys(providerId?: string) {
  const keys: (readonly unknown[])[] = [list("LEDGER_PROVIDERS_LIST")];
  if (providerId) keys.push(QUERY_KEYS.LEDGER_PROVIDER_DETAIL(providerId));
  else keys.push(list("LEDGER_PROVIDER_DETAIL"));
  return keys;
}

export function ledgerMappingCacheKeys(productProviderMappingId?: string) {
  const keys: (readonly unknown[])[] = [
    list("LEDGER_PRODUCT_PROVIDER_MAPPINGS_LIST"),
  ];
  if (productProviderMappingId) {
    keys.push(
      QUERY_KEYS.LEDGER_PRODUCT_PROVIDER_MAPPING_DETAIL(
        productProviderMappingId
      )
    );
  } else {
    keys.push(list("LEDGER_PRODUCT_PROVIDER_MAPPING_DETAIL"));
  }
  return keys;
}

export function notificationTemplateCacheKeys(
  code?: string,
  channel?: string,
  language?: string
) {
  const keys: (readonly unknown[])[] = [list("NOTIFICATION_TEMPLATES_LIST")];
  if (code && channel && language) {
    keys.push(QUERY_KEYS.NOTIFICATION_TEMPLATE_DETAIL(code, channel, language));
  } else {
    keys.push(list("NOTIFICATION_TEMPLATE_DETAIL"));
  }
  return keys;
}

/** Phase 14 — Registre de routage NotificationProviders (guide v2.3 §6.7). */
export function notificationProviderCacheKeys(notificationProviderId?: string) {
  const keys: (readonly unknown[])[] = [list("NOTIFICATION_PROVIDERS_LIST")];
  if (notificationProviderId) {
    keys.push(QUERY_KEYS.NOTIFICATION_PROVIDER_DETAIL(notificationProviderId));
  } else {
    keys.push(list("NOTIFICATION_PROVIDER_DETAIL"));
  }
  return keys;
}

export function adminApprovalCacheKeys(approvalRequestId?: string) {
  const keys: (readonly unknown[])[] = [list("ADMIN_APPROVAL_REQUESTS_LIST")];
  if (approvalRequestId) {
    keys.push(QUERY_KEYS.ADMIN_APPROVAL_REQUEST_DETAIL(approvalRequestId));
  } else {
    keys.push(list("ADMIN_APPROVAL_REQUEST_DETAIL"));
  }
  return keys;
}

export function adminApiClientCacheKeys(apiClientId?: string) {
  const keys: (readonly unknown[])[] = [list("ADMIN_API_CLIENTS_LIST")];
  if (apiClientId) {
    keys.push(QUERY_KEYS.ADMIN_API_CLIENT_DETAIL(apiClientId));
  } else {
    keys.push(list("ADMIN_API_CLIENT_DETAIL"));
  }
  return keys;
}

export function adminSystemConfigCacheKeys(key?: string) {
  const keys: (readonly unknown[])[] = [QUERY_KEYS.ADMIN_SYSTEM_CONFIGS_LIST()];
  if (key) keys.push(QUERY_KEYS.ADMIN_SYSTEM_CONFIG_DETAIL(key));
  else keys.push(list("ADMIN_SYSTEM_CONFIG_DETAIL"));
  return keys;
}

/**
 * Phase 18 — Coffres d'épargne (guide v3.1 §6.10). Un mouvement (deposit /
 * withdraw) touche aussi les soldes wallet côté client, mais le back-office
 * ne déplace jamais l'argent d'un client : on invalide le périmètre savings
 * (+ liste wallets, les soldes du portefeuille principal bougent).
 */
export function savingsVaultCacheKeys(vaultId?: string) {
  const keys: (readonly unknown[])[] = [
    list("SAVINGS_VAULTS_LIST"),
    list("SAVINGS_SUMMARY"),
  ];
  if (vaultId) {
    keys.push(
      QUERY_KEYS.SAVINGS_VAULT_DETAIL(vaultId),
      QUERY_KEYS.SAVINGS_VAULT_MOVEMENTS(vaultId),
      QUERY_KEYS.SAVINGS_VAULT_LOCK_EVENTS(vaultId)
    );
  } else {
    keys.push(
      list("SAVINGS_VAULT_DETAIL"),
      list("SAVINGS_VAULT_MOVEMENTS"),
      list("SAVINGS_VAULT_LOCK_EVENTS")
    );
  }
  return keys;
}

/**
 * Phase 22 — toute mutation sur une paie de masse invalide la liste et le
 * tableau de bord. Si `batchId` est connu, on invalide aussi son détail ; sinon
 * on invalide tous les détails (prefix-match par la liste des clés).
 */
export function payrollBatchCacheKeys(batchId?: string) {
  const keys: (readonly unknown[])[] = [
    list("PAYROLL_BATCHES_LIST"),
    list("PAYROLL_BATCH_STATS"),
  ];
  if (batchId) {
    keys.push(QUERY_KEYS.PAYROLL_BATCH_DETAIL(batchId));
  } else {
    keys.push(list("PAYROLL_BATCH_DETAIL"));
  }
  return keys;
}
